// Firebase Configuration
// Die Werte werden aus Umgebungsvariablen geladen (siehe .env Datei)

import { initializeApp, getApp } from 'firebase/app'
import { getFirestore, initializeFirestore, enableMultiTabIndexedDbPersistence, type Firestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getAuth } from 'firebase/auth'
import { logger } from '@/utils/logger'

// Firebase-Konfiguration aus Umgebungsvariablen
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

// Validierung der Firebase-Konfiguration
const requiredKeys = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
] as const;

const missingKeys = requiredKeys.filter(key => !import.meta.env[key]);

if (missingKeys.length > 0) {
  const errorMsg = `Kritische Konfiguration fehlt: ${missingKeys.join(', ')}. Bitte prüfe deine .env Datei.`;
  // Wir verwenden hier console.error, da der Logger evtl. noch nicht initialisiert ist
  // oder selbst auf diese Config angewiesen ist.
  console.error(errorMsg);
  // In Produktion werfen wir einen Fehler, um Fehlfunktionen zu vermeiden
  if (import.meta.env.PROD) {
    throw new Error(errorMsg);
  }
}

// Separate Variable für Performance-Tools (PageSpeed, Lighthouse)
// Diese müssen die Seite rendern können, aber Firestore-Verbindungen führen zu Timeouts
// WICHTIG: Diese müssen ZUERST geprüft werden, bevor isBot
export const isPerformanceTool = (() => {
  try {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    
    // PRIORITÄT 1: navigator.webdriver ist der zuverlässigste Indikator
    // Lighthouse/PageSpeed setzen dies IMMER auf true
    let isWebdriver = false;
    try {
      isWebdriver = typeof navigator !== 'undefined' && 
        'webdriver' in navigator && 
        (navigator as any).webdriver === true;
    } catch (e) {
      // Ignoriere Fehler beim Zugriff auf webdriver
      isWebdriver = false;
    }
    
    // PRIORITÄT 2: User-Agent Patterns
    // Erweitert für Desktop PageSpeed Insights und andere Tools
    const uaMatch = /Lighthouse|Chrome-Lighthouse|PageSpeed|PSTS|HeadlessChrome|GTmetrix|Pingdom|webpagetest|Speed Insights|moto g power|Gleam|Dareboost|Inspectlet/i.test(ua);
    
    // PRIORITÄT 3: Zusätzliche Indikatoren für automatisierte Browser
    let hasAutomationIndicators = false;
    try {
      const hasNoPlugins = navigator.plugins && navigator.plugins.length === 0;
      const hasNoChrome = typeof window !== 'undefined' && !(window as any).chrome;
      
      // PageSpeed Desktop hat oft spezifische Bildschirmgrößen oder Farbtiefen
      // Dies ist ein schwächerer Indikator, aber in Kombination nützlich
      const isHeadlessScreen = window.screen.width < 100 || window.screen.height < 100 || window.screen.colorDepth === 0;

      // Kombiniere Indikatoren
      hasAutomationIndicators = isWebdriver || (hasNoPlugins && hasNoChrome) || isHeadlessScreen;
    } catch (e) {
      hasAutomationIndicators = false;
    }
    
    // PRIORITÄT 4: Chrome-Lighthouse spezifisch (Desktop)
    // Manchmal ist der UA "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 ... Chrome/..."
    // aber es läuft in einer Headless-Umgebung.
    // Wir prüfen auf fehlende Sprachen oder spezifische fehlende Features
    let isSuspiciousDesktop = false;
    try {
        if (!navigator.languages || navigator.languages.length === 0) {
            isSuspiciousDesktop = true;
        }
    } catch(e) {
        // ignore
    }

    // Kombiniere alle Indikatoren
    const isPerf = isWebdriver || uaMatch || hasAutomationIndicators || isSuspiciousDesktop;
    
    // Debug-Log für Produktion
    if (typeof window !== 'undefined') {
        // Wir loggen IMMER den Status, um Debugging zu erleichtern (nur Info level)
        const logMsg = `[Firebase Detection] PerfTool: ${isPerf}, Webdriver: ${isWebdriver}, UA: ${uaMatch}, Suspicious: ${isSuspiciousDesktop}`;
        
        // Wenn erkannt, dann Warnung/Info damit man es sieht
        if (isPerf) {
             console.log(logMsg);
        } else {
            // Auch bei nicht-erkennung kurz loggen falls wir debuggen müssen
            // (kann später entfernt werden, aber jetzt wichtig für Diagnose)
             /* console.debug(logMsg); */ 
        }

        if (isPerf) {
             console.log('[Firebase] Performance-Tool erkannt:', { 
                isWebdriver,
                uaMatch, 
                hasAutomationIndicators,
                isSuspiciousDesktop,
                ua: ua.substring(0, 150) 
             });
        }
    }
    
    return isPerf;
  } catch (error) {
    return false;
  }
})();

// Prüfe ob wir von einem Bot/Crawler aufgerufen werden
// WICHTIG: Performance-Tools (PageSpeed/Lighthouse) werden JETZT als "shouldSkipFirestore" behandelt
// um Long-Polling-Timeouts zu vermeiden
export const isBot = (() => {
  try {
    // Prüfe auf Browser-Umgebung
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return true; // Server-Side Rendering oder Node.js-Umgebung
    }
    
    const ua = navigator.userAgent || '';
    
    // Bot-Patterns für echte Crawler
    const botPatterns = [
      /googlebot/i,        // Google Search Bot
      /bingbot/i,          // Bing Bot
      /slurp/i,            // Yahoo Bot
      /duckduckbot/i,      // DuckDuckGo Bot
      /baiduspider/i,      // Baidu Bot
      /yandexbot/i,        // Yandex Bot
      /facebookexternalhit/i,
      /Twitterbot/i,
      /LinkedInBot/i,
      /WhatsApp/i,
      /Applebot/i,
      /ia_archiver/i,      // Wayback Machine
      /Sogou/i,
      /Exabot/i,
      /AhrefsBot/i,
      /SemrushBot/i,
      /MJ12bot/i,
      /DotBot/i
    ];
    
    // Prüfe User-Agent
    if (botPatterns.some(pattern => pattern.test(ua))) {
      return true;
    }
    
    // Prüfe auf fehlende Browser-Features (nur wenn wirklich keine vorhanden)
    // WICHTIG: Try-Catch um sicherzustellen, dass kein Fehler geworfen wird
    try {
      if (!window.localStorage && !window.sessionStorage && !window.indexedDB) {
        return true;
      }
    } catch (e) {
      // Ignoriere Fehler beim Zugriff auf Storage
    }
    
    return false;
  } catch (error) {
    // Falls ein Fehler auftritt, behandele es NICHT als Bot
    // Dies stellt sicher, dass die App auch bei Fehlern rendert
    return false;
  }
})();

// Kombinierte Prüfung: Sollte Firebase/Firestore übersprungen werden?
// Gilt für Bots UND Performance-Tools (um Long-Polling-Timeouts zu vermeiden)
// WICHTIG: Zusätzliche Runtime-Prüfung für webdriver, falls die Initial-Prüfung versagt
export const shouldSkipFirestore = (() => {
  // Basis-Prüfung
  const baseCheck = isBot || isPerformanceTool;
  
  // Zusätzliche Runtime-Prüfung: Wenn webdriver=true, IMMER überspringen
  // Dies ist ein Fallback für den Fall, dass die Initial-Prüfung versagt
  try {
    if (typeof navigator !== 'undefined' && 'webdriver' in navigator) {
      const webdriverValue = (navigator as any).webdriver;
      if (webdriverValue === true) {
        // webdriver=true bedeutet definitiv einen automatisierten Browser
        return true;
      }
    }
  } catch (e) {
    // Ignoriere Fehler
  }
  
  return baseCheck;
})();

// Legacy-Export für Rückwärtskompatibilität
// ACHTUNG: isLighthouse wird jetzt als Performance-Tool behandelt, nicht als Bot
export const isLighthouse = isPerformanceTool;

// Initialize Firebase - prüfe ob bereits initialisiert (für besseres Lazy Loading)
// Dies verhindert Race Conditions, wenn mehrere Module gleichzeitig geladen werden
// WICHTIG: Für Bots UND Performance-Tools (PageSpeed) überspringen wir Firebase,
// um Long-Polling-Timeouts zu vermeiden
// WICHTIG: Try-Catch um sicherzustellen, dass Fehler die App nicht blockieren
let app: ReturnType<typeof initializeApp> | null = null

try {
  if (!shouldSkipFirestore) {
    // Initialisiere Firebase nur für normale Nutzer (nicht für Bots/Performance-Tools)
    try {
      // Versuche zuerst, eine bereits existierende App zu holen
      app = getApp()
    } catch {
      // App existiert noch nicht, initialisiere sie
      app = initializeApp(firebaseConfig)
    }
  } else {
    // Für Bots und Performance-Tools: Keine Firebase-Initialisierung
    // Dies verhindert Firestore Long-Polling-Timeouts bei PageSpeed
    if (isPerformanceTool) {
      logger.debug('Performance-Tool erkannt (PageSpeed/Lighthouse) - Firebase wird nicht initialisiert');
    } else {
      logger.debug('Bot erkannt - Firebase wird nicht initialisiert');
    }
  }
} catch (error) {
  // Falls Firebase-Initialisierung fehlschlägt, blockiere die App NICHT
  // Die App sollte auch ohne Firebase funktionieren können
  console.warn('[Firebase] Initialisierung fehlgeschlagen, App läuft ohne Firebase:', error);
  app = null;
}

// RADIKALER ANSATZ: Firestore wird NUR initialisiert, wenn webdriver=false ist
// Prüfe webdriver SOFRORT beim Import - bevor irgendetwas anderes passiert
const isWebdriverNow = (() => {
  try {
    return typeof navigator !== 'undefined' && 
           'webdriver' in navigator && 
           (navigator as any).webdriver === true;
  } catch {
    return false;
  }
})();

// Wenn webdriver=true, initialisiere Firestore NIEMALS
let db: Firestore | null = null

if (!isWebdriverNow && !shouldSkipFirestore && app) {
  try {
    // Initialisiere Firestore mit optimierten Settings
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      ignoreUndefinedProperties: true
    })

    // Aktiviere Offline-Persistenz für normale Browser
    if (typeof window !== 'undefined') {
      enableMultiTabIndexedDbPersistence(db).catch((err) => {
        if (err.code === 'failed-precondition') {
          logger.debug('Firestore Persistenz konnte nicht aktiviert werden: Mehrere Tabs offen');
        } else if (err.code === 'unimplemented') {
          logger.debug('Firestore Persistenz wird von diesem Browser nicht unterstützt');
        }
      });
    }
  } catch (error) {
    // Fallback: Wenn initializeFirestore fehlschlägt, verwende getFirestore
    if (app) {
      try {
        db = getFirestore(app)
      } catch (fallbackError) {
        console.warn('[Firestore] Initialisierung fehlgeschlagen:', fallbackError);
        db = null;
      }
    }
  }
} else {
  if (isWebdriverNow) {
    console.log('[Firebase] webdriver=true erkannt beim Import - Firestore wird NIEMALS initialisiert');
  } else if (shouldSkipFirestore) {
    console.log('[Firebase] Bot/Performance-Tool erkannt - Firestore wird übersprungen');
  }
}

export { db }

// Initialize Storage (nur wenn App initialisiert)
export const storage = app ? getStorage(app) : null as any

// Initialize Auth (nur wenn App initialisiert)
export const auth = app ? getAuth(app) : null as any

// Debug-Logging für Bot-Erkennung
// In Produktion auch loggen, um PageSpeed-Probleme zu debuggen
if (typeof window !== 'undefined') {
  // Globale Variable für Debug-Zugriff
  (window as any).__FIREBASE_DEBUG__ = {
    isBot,
    isPerformanceTool,
    shouldSkipFirestore,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    webdriver: typeof navigator !== 'undefined' && 'webdriver' in navigator ? (navigator as any).webdriver : 'N/A',
    plugins: typeof navigator !== 'undefined' && navigator.plugins ? navigator.plugins.length : 'N/A',
    firebaseInitialized: !!app,
    firestoreInitialized: !!db
  };
  
  // Logge immer wenn Firebase übersprungen wird (für PageSpeed-Diagnose)
  if (shouldSkipFirestore) {
    console.log('[Firebase] SKIPPED - Bot/Performance-Tool detected:', {
      isBot,
      isPerformanceTool,
      webdriver: typeof navigator !== 'undefined' && 'webdriver' in navigator ? (navigator as any).webdriver : false,
      userAgent: navigator.userAgent?.substring(0, 150)
    });
  } else {
    // Logge auch wenn NICHT übersprungen wird (für Debugging)
    // Nur wenn webdriver=true, damit wir sehen können, ob die Erkennung versagt
    if (typeof navigator !== 'undefined' && 'webdriver' in navigator && (navigator as any).webdriver === true) {
      console.warn('[Firebase] WARNING: webdriver=true aber Performance-Tool nicht erkannt!', {
        userAgent: navigator.userAgent?.substring(0, 150),
        webdriver: (navigator as any).webdriver
      });
    }
  }
}

export default app
